import axios from 'axios';
import type {
  SelfAxiosRequestConfig,
  SelfResponse,
  HttpResponse,
} from '@/types/request';
import { ActionType } from '@/types/request';
import { Message, Modal } from '@arco-design/web-vue';
import { useUserStore, useSupplierStore } from '@/store';
import { BASE_URL } from '@/utils/global';
import { version } from '@/config/app-version';
import { isLogin } from '@/utils/auth';
import useTabbar from '@/hooks/tabbar';
import { handleUpdateFailMessage } from '@/hooks/useSSE/common';

const instance: any = axios.create({
  baseURL: BASE_URL.webServer, // api的base_url
  timeout: 300000, // 请求超时时间
  withCredentials: true, // 允许携带cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// if (import.meta.env.VITE_API_BASE_URL) {
//   axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;
// }
export const setHeaders = (config: any, ignoreUrl = false) => {
  const userStore = useUserStore();
  const { userLoginInfo } = userStore;
  const { supplierInfo, clientInfo, getLoginSupplier } = useSupplierStore();
  if (config.headers) {
    if (config.url || ignoreUrl) {
      if (isLogin() && userLoginInfo) {
        // 设置请求头token  scm-cid 和 scm-uid
        config.headers['scm-token'] = userLoginInfo.token;
        config.headers['scm-cid'] = userLoginInfo.cid;
        config.headers['scm-uid'] = userLoginInfo.id;
      } else if (getLoginSupplier() && supplierInfo) {
        // 加工厂接口设置token、cid 和 sid
        config.headers['scm-s-token'] = supplierInfo.token;
        if (clientInfo) {
          config.headers['scm-wax-cid'] = clientInfo.cid;
          config.headers['scm-wax-sid'] = clientInfo.supplierId;
        }
      }
    }
    config.headers['qs-referer'] = 'scmAppJson';
    config.headers['scm-pc-version'] = version;
  }
  return config;
};

instance.interceptors.request.use(
  (config: SelfAxiosRequestConfig) => {
    // let each request carry token
    // this example using the JWT token
    // Authorization is a custom headers key
    // please modify it according to the actual situation
    // const excludeUrl = [`${BASE_URL.scmServer}/api/admin/account/login.ac`];

    return setHeaders(config);
  },
  (error: any) => {
    // do something
    return Promise.reject(error);
  }
);

// add response interceptors
instance.interceptors.response.use(
  async (response: SelfResponse<HttpResponse>) => {
    const res = response.data;
    // if the custom code is not 20000, it is judged as an error.
    if (
      res.status === 200 ||
      (res?.statusCode && [1, 2, 3, 5].includes(res.statusCode)) ||
      response.config.noErrorPop ||
      ['blob', 'arraybuffer'].includes(response.request?.responseType ?? '')
    ) {
      if (response.config.responseOriginData) {
        // amis返回response处理数据
        return response;
      }
      // is的statusCode情况待完善
      if (res.status === 200 && res.message) {
        // 客户端附加行为
        if (res.action === ActionType.alert) {
          await new Promise((resolve) => {
            Message.error({
              content: res.message as string,
              duration: 2 * 1000,
              onClose: () => resolve(res),
            });
          });
        } else if (res.action === ActionType.dialog) {
          await new Promise((resolve) => {
            Modal.info({
              title: '提示',
              hideCancel: true,
              maskClosable: false,
              content: res.message as string,
              onClose: () => resolve(res),
            });
          });
        }
      }
      return res;
    }
    // 目前触发条件：在进销存修改了商品的颜色或尺码，生产开单的时候选中该商品同时勾选创建采购单
    if (res.status === 411) {
      Modal.error({
        title: '操作失败',
        hideCancel: true,
        maskClosable: false,
        content: res.message || '请求发生错误',
      });
    } else if (res.action === ActionType.UPDATE_FAIL_ON_LOCK_EFFECT) {
      // 全量更新(商品/设计款/版单)失败的message处理
      handleUpdateFailMessage(res.message as string);
    } else if (!response.config.noErrorToast) {
      // 低代码（设置了noErrorToast=true）的提示由amis自己提示，包括无权限
      Message.error({
        content: (res.message ?? res.content ?? '请求发生错误') as string,
        duration: 2 * 1000,
      });
    }
    if (res.status === 410) {
      Message.error({
        content: (res.message ?? res.content ?? '请求发生错误') as string,
        duration: 2 * 1000,
      });
      if (response.config.headers['scm-s-token']) {
        const supplierStore = useSupplierStore();
        // 登录失效 todo
        await supplierStore.logout();
      } else {
        const userStore = useUserStore();
        // 登录失效 todo
        await userStore.logout();
      }
    }
    if (res.action === ActionType.goToNotFound) {
      // 无权限接口的重定向处理
      const { handleNoPermission } = useTabbar();
      handleNoPermission();
    }
    return Promise.reject(
      new CustomHttpError(
        (res.message ?? res.content ?? '请求发生错误') as string,
        {
          cause: 'http error',
          data: res.data,
          action: res.action,
          status: res.status,
        }
      )
    );
  },
  async (error: any) => {
    const res = error.response?.data;
    if (error.config && error.config.errorSilence) {
      return Promise.reject(error);
    }
    if (error.response && error.response.status === 403) {
      // 如果服务端返回1403则认为当前请求资源要求登录，而当前会话未登录，跳至登录页面
      if (error.response.data && error.response.data.statusCode === 1403) {
        // 未登录
        const userStore = useUserStore();
        // 登录失效 todo
        await userStore.logout();
        // 不确定 app reload 会有什么问题
        // window.location.reload()
        return Promise.reject(error);
      }
      if (error.response.data && error.response.data.statusCode === 1401) {
        // 无权限
        // 跳转到无权限页面
        return Promise.reject(error);
      }
      if (error.response.data && error.response.data.content) {
        // 其他情况弹出错误提示
        // errorHandle(error);
      }
    } else if (axios.isCancel(error)) {
      // 主动取消的请求
      Message.warning({
        content: (error?.message ?? error?.content ?? '请求发生错误') as string,
        duration: 2 * 1000,
      });
    } else if (
      !error.response?.config.noErrorToast ||
      error.response?.config.responseOriginData
    ) {
      // responseOriginData为true代表amis接口，接口报错时提示
      Message.error({
        content: (res?.message ?? res?.content ?? '请求发生错误') as string,
        duration: 2 * 1000,
      });
      // errorHandle(error);
    }
    return Promise.reject(error);
  }
);

// 提供iframe调用
window.iframeRequest = (data: Recordable) => {
  data.url = BASE_URL.scmServer + data.url;
  return instance(data);
};

class CustomHttpError extends Error {
  status: number;

  action: ActionType;

  data?: Recordable;

  cause: string;

  constructor(message: string, option?: Recordable) {
    super(message);
    this.cause = option?.cause;
    this.status = option?.status;
    this.action = option?.action;
    this.data = option?.data;
  }
}

/**
 * 创建带冷却期的请求函数
 * @param {Function} requestFn - 原始请求函数 (如 axios.get/post)
 * @param {number} cooldownMs - 冷却时间（毫秒）
 * @returns {Function} 封装后的安全请求函数
 */
export function createCooldownRequest(requestFn) {
  // 缓存进行中的请求的CancelToken：{ url: cancelTokenSource }
  const pendingRequests = new Map();

  return async function (args, cooldownMs = 120000) {
    const config = args || {};
    const { url } = config;

    // 检查冷却期
    const lastSuccess = localStorage.getItem(`cooldown_${url}`);
    if (lastSuccess && Date.now() - lastSuccess < cooldownMs) {
      const remain = Math.ceil(
        (cooldownMs - (Date.now() - lastSuccess)) / 1000
      );
      Message.warning({
        content: `请勿频繁请求，${remain}秒后可重试`,
        duration: 2 * 1000,
      });
      throw new Error(`请勿频繁请求，${remain}秒后可重试`);
    }
    // 取消前一个未完成的相同请求
    if (pendingRequests.has(url)) {
      pendingRequests.get(url).cancel('请勿重复请求');
    }

    // 创建新的CancelToken
    const cancelTokenSource = axios.CancelToken.source();
    pendingRequests.set(url, cancelTokenSource);

    try {
      const response = await requestFn({
        ...config,
        cancelToken: cancelTokenSource.token,
      });

      // 仅在成功时更新冷却期
      localStorage.setItem(`cooldown_${url}`, Date.now());
      return response;
    } catch (error) {
      // 如果是主动取消的请求，不重置冷却期
      if (!axios.isCancel(error)) {
        localStorage.setItem(`cooldown_${url}`, null);
        localStorage.removeItem(`cooldown_${url}`); // 可选：失败时允许立即重试
      }
      throw error;
    } finally {
      // 删除缓存的CancelToken
      pendingRequests.delete(url);
    }
  };
}

// 处理noErrorToast=true的报错提示
export const errorToast = (error: any) => {
  if (error.isAxiosError) {
    // http错误
    Message.error(error?.response?.data?.message || '操作失败！');
  } else if (error.status !== 411) {
    // 业务报错
    Message.error(error.message || '操作失败！');
  }
};
export default instance;
